// Profile page (profile spec §3.1, §3.2).
//
// Structure comes straight from the spec:
//
//   TopBar    個人檔案 (display-2) + 設定入口
//   Segment   我 / TA        ← settled selection = ink-1 近黑填充 (DESIGN §14)
//   ├ 身份卡  頭像 + 名字 / 出生日期 / 今年 N 歲 / 星座 / bio / email
//   ├ 生日提醒 提前天數 chips + 鎖定的生日日期
//   └ Nav
//
// Three rules the page encodes, which is why it is more than a layout:
//
// * **A birthday belongs to the member who was born on it** (§2.1 P1). The TA
//   tab renders the date read-only with a lock, while its *reminder days stay
//   editable* — the two are in different sections on purpose.
// * **No sign and no age without a birthday** (§3.2). Both are derived, and
//   the card says so rather than rendering `星座: —`.
// * **A paired member with no birthday gets a backfill CTA** (§3.2, §5.4).
//   Filling it in fires `user.profile_updated`, whose listener runs the
//   birthday fan-out, so the anniversary appears without anything else being
//   replayed. That is the promise BR-15's comment made and never delivered.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Lock, Mail, Settings } from "lucide-react";
import { PageHero } from "@/components/primitives/PageHero";
import { SegmentedControl } from "@/components/primitives/SegmentedControl";
import { AuthorAvatar } from "@/components/primitives/AuthorAvatar";
import { InfoBanner } from "@/components/primitives/InfoBanner";
import { ErrorState } from "@/components/primitives/ErrorState";
import { LoadingState } from "@/components/primitives/LoadingState";
import { Card, CardSection } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { PageContainer, SectionStack } from "@/components/layout";
import { AvatarEditor } from "@/components/profile/AvatarEditor";
import { BirthdayPicker } from "@/components/profile/BirthdayPicker";
import { BirthdayBackfillCta } from "@/components/profile/BirthdayBackfillCta";
import { ReminderDaysField } from "@/components/profile/ReminderDaysField";
import {
  avatarUrl,
  getMyProfile,
  getPartnerProfile,
  updateMyProfile,
  type ProfileRead,
} from "@/lib/profile/api";
import { daysUntilBirthday } from "@/lib/profile/zodiac";
import { nonLeapChoiceFrom } from "@/lib/profile/nonLeap";
import { ApiError } from "@/lib/api/pair";
import { usePartnerId } from "@/lib/profile/usePartnerId";

type Tab = "self" | "partner";

export function ProfilePage() {
  const partnerId = usePartnerId();
  const [tab, setTab] = useState<Tab>("self");
  const [me, setMe] = useState<ProfileRead | null>(null);
  const [partner, setPartner] = useState<ProfileRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setMe(await getMyProfile());
      // A missing partner is a normal state, not an error: unpaired users are
      // redirected out of the app shell, but a dissolve in flight would
      // otherwise blank the whole page.
      if (partnerId) {
        setPartner(await getPartnerProfile(partnerId));
      }
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "個人資料載入失敗");
    } finally {
      setLoading(false);
    }
  }, [partnerId]);

  useEffect(() => {
    void load();
  }, [load]);

  const current = tab === "self" ? me : partner;
  const isSelf = tab === "self";

  return (
    <div data-testid="profile-page">
      <PageHero
        title="個人檔案"
        trailing={
          <a
            href="/settings"
            aria-label="設定"
            data-testid="profile-settings-link"
            className="tap-target press flex items-center justify-center rounded-sm text-ink-2 hover:bg-black/5"
          >
            <Settings size={20} strokeWidth={1.5} aria-hidden="true" />
          </a>
        }
      />

      <PageContainer vertical="default">
        <SectionStack density="default">
          {loading ? <LoadingState shape="card" count={2} /> : null}
          {error ? <ErrorState title="個人資料載入失敗" description={error} onRetry={() => void load()} /> : null}

          {!loading && !error && me ? (
            <>
              <SegmentedControl<Tab>
                label="選擇查看的檔案"
                value={tab}
                onChange={setTab}
                options={[
                  { value: "self", label: "我" },
                  { value: "partner", label: "TA", hint: partner ? partner.display_name : undefined },
                ]}
              />

              {tab === "partner" && !partner ? (
                <InfoBanner tone="paper-deep">你們還沒有配對，所以看不到 TA 的檔案。</InfoBanner>
              ) : null}

              {current ? (
                <IdentityCard
                  profile={current}
                  editable={isSelf}
                  editing={isSelf && editing}
                  onStartEdit={() => setEditing(true)}
                  onCancelEdit={() => setEditing(false)}
                  onSaved={(next) => {
                    setMe(next);
                    setEditing(false);
                  }}
                  refresh={load}
                />
              ) : null}

              {current ? (
                <ReminderDaysField
                  profile={current}
                  canEdit={Boolean(current.birthday_anniversary_id)}
                  onChanged={() => void load()}
                />
              ) : null}
            </>
          ) : null}
        </SectionStack>
      </PageContainer>
    </div>
  );
}

function IdentityCard({
  profile,
  editable,
  editing,
  onStartEdit,
  onCancelEdit,
  onSaved,
  refresh,
}: {
  profile: ProfileRead;
  editable: boolean;
  editing: boolean;
  onStartEdit: () => void;
  onCancelEdit: () => void;
  onSaved: (next: ProfileRead) => void;
  /** Re-read the profile; the avatar endpoints are not a PATCH of the profile. */
  refresh: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const days = useMemo(() => daysUntilBirthday(profile.birthday), [profile.birthday]);
  const choice = nonLeapChoiceFrom(
    profile.birthday_non_leap_month,
    profile.birthday_non_leap_day,
  );

  const ageLine = (() => {
    if (!profile.birthday || profile.age_turning === null) return null;
    const today = new Date();
    const hadBirthday =
      today.getMonth() + 1 > Number(profile.birthday.slice(5, 7)) ||
      (today.getMonth() + 1 === Number(profile.birthday.slice(5, 7)) &&
        today.getDate() >= Number(profile.birthday.slice(8, 10)));
    // Spec §2.1: the number does not change mid-year, only the wording does.
    return hadBirthday ? `今年 ${profile.age_turning} 歲` : `今年將滿 ${profile.age_turning} 歲`;
  })();

  return (
    <Card data-testid="profile-identity-card">
      <CardSection className="flex items-start gap-s-4">
        <AuthorAvatar
          tone={profile.relation === "self" ? "you" : "partner"}
          size="lg"
          label={profile.display_name}
          src={avatarUrl(profile.avatar_url)}
        />
        <div className="min-w-0 flex-1">
          {/* `line-clamp-2` — a display name is content the user chose, not a
              label. At 320px this measured 92px against a 123px name, so
              `truncate` cut a quarter of it. Two lines reads; the rest of the
              profile (email, birthday, settings) is unchanged. */}
          <p className="text-h3 text-ink-1 line-clamp-2 text-balance">{profile.display_name}</p>
          {profile.birthday ? (
            <p className="text-body-sm text-ink-2 tabular-nums mt-0.5" data-testid="profile-birthday">
              <span className="whitespace-nowrap">{profile.birthday.replace(/-/g, " / ")}</span>
              {ageLine ? (
                // `whitespace-nowrap`: a number and its counter must not split
                // across lines. At 390px the date plus 「今年 30 歲」 overflows
                // otherwise, and CJK line breaking happily puts 歲 on its own
                // line — which reads as a typo rather than a wrap.
                <span className="ml-s-2 text-ink-3 whitespace-nowrap">{ageLine}</span>
              ) : null}
            </p>
          ) : (
            <p className="text-body-sm text-ink-3 mt-0.5" data-testid="profile-no-birthday">
              星座與年齡在填上生日後出現
            </p>
          )}
          {profile.birthday && profile.zodiac ? (
            <p className="text-meta text-ink-3 mt-0.5" data-testid="profile-zodiac">
              {profile.zodiac}
              {days !== null ? <span className="ml-s-2 tabular-nums">還有 {days} 天</span> : null}
            </p>
          ) : null}
          {choice ? (
            <p className="text-meta text-ink-3 mt-0.5" data-testid="profile-non-leap">
              平年當作 {choice.month === 2 ? "2 月 28 日" : "3 月 1 日"}
            </p>
          ) : null}
        </div>
        {editable && !editing ? (
          <Button variant="secondary" size="sm" onClick={onStartEdit} data-testid="profile-edit">
            編輯
          </Button>
        ) : null}
      </CardSection>

      {/* Profile spec §3.2 puts the backfill CTA on the identity card, not
          only inside the editor. Requiring a tap on 編輯 first means a person
          without a birthday sees "星座與年齡在填上生日後出現" and has to guess
          that a button is the next step. The CTA opens the editor, so this is
          one tap either way — it just says what the tap does. */}
      {editable && !profile.birthday && !editing ? (
        <CardSection className="pt-0">
          <BirthdayBackfillCta variant="inline" onClick={onStartEdit} />
        </CardSection>
      ) : null}

      {profile.bio ? (
        <CardSection className="pt-0">
          <p className="text-body text-ink-2" data-testid="profile-bio">
            {profile.bio}
          </p>
        </CardSection>
      ) : null}

      <CardSection className="pt-0 flex flex-col gap-s-1">
        <p className="flex items-center gap-s-2 text-body-sm text-ink-2 truncate" data-testid="profile-email">
          <Mail size={16} strokeWidth={1.5} aria-hidden="true" className="shrink-0" />
          {profile.email ?? "—"}
        </p>
      </CardSection>

      {!editable ? (
        <CardSection className="pt-0">
          <p className="flex items-center gap-s-1 text-meta text-ink-3" data-testid="profile-partner-lock">
            <Lock size={14} strokeWidth={1.5} aria-hidden="true" />
            生日是 TA 的資料,你可以調整提醒,但日期改不了
          </p>
        </CardSection>
      ) : null}

      {editing ? (
        <CardSection className="pt-0 flex flex-col gap-s-3" data-testid="profile-editor">
          <AvatarEditor
            displayName={profile.display_name}
            avatarUrlValue={profile.avatar_url}
            tone={profile.relation === "self" ? "you" : "partner"}
            onChanged={refresh}
          />
          <BirthdayEditor
            profile={profile}
            busy={busy}
            error={saveError}
            onCancel={onCancelEdit}
            onSubmit={async (patch) => {
              setBusy(true);
              setSaveError(null);
              try {
                onSaved(await updateMyProfile(patch));
              } catch (err) {
                setSaveError(
                  err instanceof ApiError ? err.message : "儲存失敗,請再試一次",
                );
              } finally {
                setBusy(false);
              }
            }}
          />
        </CardSection>
      ) : null}
    </Card>
  );
}

function BirthdayEditor({
  profile,
  busy,
  error,
  onCancel,
  onSubmit,
}: {
  profile: ProfileRead;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onSubmit: (patch: {
    birthday?: string;
    birthday_non_leap_month?: number;
    birthday_non_leap_day?: number;
  }) => Promise<void>;
}) {
  const [birthday, setBirthday] = useState<string | null>(profile.birthday);
  const [choice, setChoice] = useState(() =>
    nonLeapChoiceFrom(profile.birthday_non_leap_month, profile.birthday_non_leap_day),
  );
  const [pendingChoice, setPendingChoice] = useState(false);

  const isFeb29 = birthday?.slice(5, 10) === "02-29";

  return (
    <div className="flex flex-col gap-s-3">
      {!profile.birthday ? <BirthdayBackfillCta variant="inline" /> : null}
      <BirthdayPicker
        value={birthday}
        nonLeapChoice={choice}
        onChange={setBirthday}
        onNonLeapChange={(next) => {
          setChoice(next);
          setPendingChoice(true);
        }}
      />
      {isFeb29 && !choice ? (
        <p className="text-meta text-partner-strong" role="alert" data-testid="profile-non-leap-required">
          2 月 29 日出生,請選一下平年當作哪一天
        </p>
      ) : null}
      {error ? (
        <p className="text-meta text-partner-strong" role="alert" data-testid="profile-save-error">
          {error}
        </p>
      ) : null}
      <div className="flex gap-s-2">
        {/* Secondary, not primary: two primary buttons side by side say "you
            must pick one of these equally" about a Save you can also just
            leave. Cancel is the way out, not an alternative offer. */}
        <Button
          variant="secondary"
          size="md"
          disabled={busy}
          onClick={onCancel}
          data-testid="profile-cancel"
        >
          取消
        </Button>
        <Button
          variant="primary"
          size="md"
          disabled={busy || (isFeb29 && !choice)}
          data-testid="profile-save"
          onClick={() => {
            const patch: {
              birthday?: string;
              birthday_non_leap_month?: number;
              birthday_non_leap_day?: number;
            } = {};
            if (birthday) patch.birthday = birthday;
            // Sent only when it changed, so a client that did not touch the
            // substitute is never re-asked the question server-side.
            if (choice && pendingChoice) {
              patch.birthday_non_leap_month = choice.month;
              patch.birthday_non_leap_day = choice.day;
            }
            void onSubmit(patch);
          }}
        >
          儲存
        </Button>
      </div>
    </div>
  );
}
